import { AvaibookAccommodation, Integration, LocalEditableIntegration } from "../../_modelsTS/Integration";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CapsuleButton, Selector } from "../../_components";
import { useDispatch, useSelector } from "react-redux";
import { MIN_TIME, TIME_FORMAT } from "../../_constants";
import { useError } from "../../_hooks/useError";
import { Icons } from "../../_assets";
import SendInvitationURLToggle from "../../IntegrationsModule/components/SendInvitationURLToggle";
import TimePicker from "rc-time-picker";
import moment from "moment";
import { TimeHelper } from "../../_helpers";
import { IntegrationStore } from "../../_stores/IntegrationStore";
import { integrationsActions } from "../../_actions/integrations.actions";
import { Selectors } from "../../_reducers/app.reducer";
import { INVITATION_TYPES } from "../../_constants";
import { AvaibookStore } from "../../_stores/AvaibookStore";
import { avaibookActions } from "../../_actions/avaibook.actions";


type IntegrationInputProps = {
    integration: LocalEditableIntegration;
    onChangeInputs?: (...args: any) => void | any;
    onToggleEditable: (...args: any) => void | any;
    onDelete: (index: number) => void;
    index: number;
    groups: { [key: number]: any[]; }; // To be typed asap
    onSetInitialStatus: (...args: any) => void | any;
}

export function AvaibookNewIntegrationInput({ 
    integration, 
    onDelete,
    groups,
    onSetInitialStatus,
    index,
    onToggleEditable 
}: IntegrationInputProps) {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    const integrations = useSelector(IntegrationStore.selectorGetItems);
    const selectedIntegrationUid: string = useSelector(IntegrationStore.selectorGetSelectedId);
    
    const allGroups = useMemo(() => [...(groups[INVITATION_TYPES.LOCATION] ?? []), ...(groups[INVITATION_TYPES.ROOM]) ?? []], [groups]);
    const integrationGroups = integration.groups || [];
    const [selectedGroupsLocal, setSelectedGroupsLocal] = useState(integrationGroups.map(({id}) => ({value: id})) ?? []);

    const baseIntegration = useMemo(() => integration, [integration]);
    const [apikeyValue, setApikeyValue] = useState(integration.apikey ?? '');
    const [sendInvitationURL, setSendInvitationURL] = useState(integration.sendInvitationURL ?? true);
    const [hostIDValue, setHostIDValue] = useState(integration.host_id ?? '');
    const [hostNameValue, setHostNameValue] = useState(integration.host_name ?? '');
    const { errorMessage, setError, hasError, disableError } = useError();

    const [checkIn, setCheckIn] = useState(integration.check_in ?? MIN_TIME);
    const [checkOut, setCheckOut] = useState(integration.check_out ?? MIN_TIME);
    const timeNoSecondsFormat = TimeHelper.getTimeFormats().TIME_NO_SECONDS;
    const getHourAndMinutesFormat = useCallback((time: string) => moment(time, timeNoSecondsFormat), []);
    const locationUid = useSelector(Selectors.getSelectedLocationUid) ?? '';

    const buildOptions = useCallback((availableGroups: any[] = []) => {
        return availableGroups.map((group) => ({ value: group.id, label: group.name, isDisabled: group.isDisabled })) ?? [];
    }, []);

    const buildGroupGroupedOptions = useCallback((groups: { [key: number]: any[] }) => {
        return [
            {
                label: t("global_typeof_location"),
                options: buildOptions(groups[INVITATION_TYPES.LOCATION]),
            },
            {
                label: t("global_typeof_room"),
                options: buildOptions(groups[INVITATION_TYPES.ROOM]),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [availableGroups, setAvailableGroups] = useState(buildGroupGroupedOptions(groups));

    const onSeeReservations = useCallback(async (integrationUid) => {
        await dispatch(integrationsActions.listReservations(locationUid, integrationUid));
        dispatch(IntegrationStore.actionSelectItem(integrationUid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationUid]);

    const onChangeGroups = useCallback(selectedGroups => {
        setAvailableGroups(buildGroupGroupedOptions(groups));
        setSelectedGroupsLocal(selectedGroups);
    }, [buildGroupGroupedOptions, groups]);

    const buildAvaibookAccommodationsOptions = useCallback((availableProperties: any[] = []) => {
        return availableProperties.map(
            (property) => (
                { 
                    value: property.id, 
                    label: property.name, 
                    isDisabled: false 
                }
            )
        ) ?? [];
    }, []);

    const avaibookAccommodations = useSelector(AvaibookStore.selectorGetItems);
    const selectedAvaibookAccommodation : AvaibookAccommodation = useSelector(AvaibookStore.selectorGetSelectedItem) ?? {id:integration.host_id, name:integration.host_name};

    const [selectedAvaibookAccommodationLocal, setSelectedAvaibookAccommodationLocal] = useState(
        { 
            value:integration.host_id, 
            label:integration.host_name, 
            isDisabled: false 
        }
    );
    const [availableAvaibookAccommodations, setAvailableAvaibookAccommodations] = useState(buildAvaibookAccommodationsOptions(avaibookAccommodations));

    const onChangeAvaibookAccommodations = useCallback(selectedProperty => {
        setSelectedAvaibookAccommodationLocal(selectedProperty);
        dispatch(AvaibookStore.actionSelectItem(selectedProperty.value));
    }, [dispatch]);

    useEffect(() => {
        setAvailableAvaibookAccommodations(buildAvaibookAccommodationsOptions(avaibookAccommodations));
    }, [avaibookAccommodations, buildAvaibookAccommodationsOptions]);

    useEffect(() => {
        setApikeyValue(integration.apikey ?? '');
        setHostIDValue(integration.host_id ?? '');
        setHostNameValue(integration.host_name ?? '');
        setSendInvitationURL(integration.sendInvitationURL ?? true);
        setCheckIn(integration.check_in ?? '00:00');
        setCheckOut(integration.check_out ?? '00:00');

        const selected = buildOptions(allGroups.filter(({ id }) => integrationGroups.map(({id}) => id).includes(id)));
        onChangeGroups(selected);
    }, [
        integration, 
        allGroups, 
        buildOptions, 
        groups,
        onChangeGroups
    ]);

    useEffect(() => {
        if (integration.uid)
            dispatch(avaibookActions.listAccommodations(integration.uid));
    }, [dispatch, integration.uid]);

    const canValidate = useMemo(() => {
        if(hasError) return false;
        return !!apikeyValue &&
            selectedGroupsLocal.length !== 0;
    }, [
        apikeyValue, 
        hasError, 
        selectedGroupsLocal
    ]);

    const onValidate = useCallback(async () => {

        if(!apikeyValue) {
            setError(t('mod_integrations_link_required'));
            return;
        }
     
        const editedIntegration: Integration = {
            apikey: apikeyValue,
            company: integration.company,
            uid: integration.uid,
            groups: selectedGroupsLocal.map(({ value }) => ({ id: value })),
            // Not user-configurable for Avaibook: delivery (online check-in + email/SMS)
            // depends on the public link; all prod Avaibook integrations run with it on.
            createPublicUrl: true,
            sendInvitationURL
        }
        if(locationUid) {
            try {
                await dispatch(integrationsActions.createIntegration(locationUid, editedIntegration));
            } catch (err: any) {
                setError(err.message)
            }
        }
    }, [dispatch, 
        setError, 
        integration.company,
        integration.uid, 
        apikeyValue, 
        locationUid, 
        selectedGroupsLocal, 
        t, 
        sendInvitationURL
    ]);

    const canSave = useMemo(() => {

        if(hasError) return false;

        const baseGroups = baseIntegration.groups || [];

        const groupsWithoutDifferences = baseGroups.every(({id}) =>
                selectedGroupsLocal.map(({ value }) => value).includes(id),
            ) && baseGroups.length === selectedGroupsLocal.length;

        return (
            !!apikeyValue && 
            !!selectedAvaibookAccommodation && 
            selectedGroupsLocal.length !== 0 && 
            (
                apikeyValue !== baseIntegration.apikey ||
                hostIDValue !== baseIntegration.host_id ||
                !groupsWithoutDifferences || 
                integration.createPublicUrl !== true ||
                sendInvitationURL !== integration.sendInvitationURL || 
                checkIn !== integration.check_in ||
                checkOut !== integration.check_out
        ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseIntegration.groups, baseIntegration.link, integrations, integration.createPublicUrl, apikeyValue, hostIDValue, selectedGroupsLocal, 
        sendInvitationURL, selectedAvaibookAccommodation, checkIn, checkOut]);

        const onSave = useCallback(async () => {

            const editedIntegration: Integration = {
                apikey: apikeyValue,
                host_id: selectedAvaibookAccommodationLocal.value,
                host_name: selectedAvaibookAccommodationLocal.label,
                company: integration.company,
                createPublicUrl: true,
                groups: selectedGroupsLocal.map(({ value }) => ({ id: value })),
                uid: integration.uid,
                sendInvitationURL,
                check_in: checkIn,
                check_out: checkOut
            }
            if(locationUid) {
                try {
                    await dispatch(integrationsActions.createIntegration(locationUid, editedIntegration));
                } catch (err: any) {
                    setError(err.message)
                }
            }
        }, [
            dispatch, 
            setError, 
            integration.company,
            integration.uid, 
            apikeyValue, 
            locationUid, 
            selectedGroupsLocal, 
            sendInvitationURL, 
            checkIn, 
            checkOut
        ]);

    return (
        <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg">
            <div
                className="col-span-full"
                style={{
                    display: "grid",
                    gap: "12px",
                    gridTemplateColumns: "3fr 2fr",
                }}
            >
                <div>Token</div>
                <input
                    className={`${hasError ? 'input-error' : ''} ${!integration.editable ? 'cna' : ''} col-span-full w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    readOnly={!integration.editable}
                    value={apikeyValue}
                    placeholder={"Token"}
                    onChange={(e) => {
                        setApikeyValue(e.target.value?.trim());
                        /*if(IntegrationsHelper.checkIfLinkExists(e.target.value, integrations) && e.target.value !== integration.link) {
                            setError(t('mod_integrations_already_have_integration_with_this_link'));
                        } else {
                            disableError();
                        }*/
                    }}
                />
            </div>
            <div
                style={{
                    display: "grid",
                    gap: "12px",
                    gridTemplateColumns: "1fr 1fr",
                }}
                className="col-span-full d-flex justify-content-between"
            >
                <div className="c-min-w-40">
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_invitations_select_group")}
                        isClearable
                        value={selectedGroupsLocal[0] ?? null}
                        onChange={(selectedGroup: any) =>
                            onChangeGroups(selectedGroup ? [selectedGroup] : [])
                        }
                        isDisabled={!integration.editable}
                        options={availableGroups}
                    />
                </div>
                <div>
                    <SendInvitationURLToggle
                        optional
                        boldedName={false}
                        initialState={sendInvitationURL}
                        readOnly={!integration.editable}
                        onChange={setSendInvitationURL}
                    />
                </div>
            </div>
            <div className="col-span-full d-flex align-items-start c-bg-grey-250 c-rounded-big p-2">
                <img
                    src={Icons.info}
                    alt="info"
                    className="mr-2 flex-shrink-0"
                    style={{ width: "18px", height: "18px" }}
                />
                <span className="small text-muted">{t("mod_integrations_avaibook_access_data_info")}</span>
            </div>
            { !!!integration.uid && 
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex">
                        <CapsuleButton
                            size="SMALL"
                            isEnabled={canValidate}
                            onClick={onValidate}
                            text={t("global_validate")}
                        />
                    </div>
                </div>
            }

            {integration.uid && 
                <>
                    <div className="d-flex align-items-center justify-content-between pr-2">
                        <div className="c-text-capitalize-first mr-2">
                            Check In
                        </div>
                        <TimePicker
                            disabled={!integration.editable}
                            showSecond={false}
                            minuteStep={5}
                            value={getHourAndMinutesFormat(checkIn)}
                            format={TIME_FORMAT}
                            onChange={(value) =>
                                setCheckIn(
                                    value
                                        ? value?.format(TIME_FORMAT)
                                        : MIN_TIME,
                                )
                            }
                            />
                    </div>
                    <div className="d-flex align-items-center justify-content-between pr-2">
                        <div className="c-text-capitalize-first mr-2">
                            Check Out
                        </div>
                        <TimePicker
                            disabled={!integration.editable}
                            showSecond={false}
                            minuteStep={5}
                            value={getHourAndMinutesFormat(checkOut)}
                            format={TIME_FORMAT}
                            onChange={(value) =>
                                setCheckOut(
                                    value
                                        ? value?.format(TIME_FORMAT)
                                        : MIN_TIME,
                                )
                            }
                        />
                    </div>
                    <div
                        className="col-span-full"
                        style={{
                            display: "grid",
                            gap: "12px",
                            gridTemplateColumns: "3fr 2fr",
                        }}
                    >
                        <Selector
                            hideSelectedOptions={false}
                            placeholder={t("mod_integrations_select_ical_url")}
                            value={selectedAvaibookAccommodationLocal}
                            onChange={(selectedAccomodation: any) =>
                                onChangeAvaibookAccommodations(selectedAccomodation)
                            }
                            isDisabled={!integration.editable || availableAvaibookAccommodations.length === 0}
                            options={availableAvaibookAccommodations}
                        />
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            {integration.host_id &&
                                <CapsuleButton
                                    isEnabled={integration.uid !== selectedIntegrationUid}
                                    onClick={() => onSeeReservations(integration.uid)}
                                    classNameExtra="mr-2"
                                    // eslint-disable-next-line react/style-prop-object
                                    size="SMALL"
                                    text={t('mod_integrations_see_reservations')}
                                />
                            }
                        </div>
                        <div className="d-flex">
                            {integration.uid &&
                                <CapsuleButton
                                    size="SMALL"
                                    classNameExtra="mr-2"
                                    style={!integration.editable ? 'PRIMARY' : 'INFO'}
                                    onClick={() => integration.editable ? onSetInitialStatus(index) : onToggleEditable(index)}
                                    text={t(!integration.editable ?  "global_edit" : 'global_cancel')}
                                />
                            }
                            {integration.uid &&
                                <CapsuleButton
                                    onClick={() => onDelete(index)}
                                    classNameExtra="mr-2"
                                    // eslint-disable-next-line react/style-prop-object
                                    style="DANGER"
                                    size="SMALL"
                                    icon={Icons.trash}
                                />
                            }
                            <CapsuleButton
                                size="SMALL"
                                isEnabled={canSave}
                                onClick={onSave}
                                text={t("global_save")}
                            />
                        </div>
                    </div>
                </>
                
            }
            
            {errorMessage && (
                <div className="text-danger mr-2">{errorMessage}</div>
            )}
        </div>
    );
}